import { useCallback } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router';

import { localStorage } from '@change-corgi/core/storage';
import { useNavigate } from '@change-corgi/core-react/router';
import { useTracking } from '@change-corgi/core-react/tracking';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { useStartAPetitionPageData, useStartAPetitionPageState } from '#src/app/pages/startAPetitionV2/pageContext';
import { deleteDraftAuthor } from '#src/app/shared/api/petitionDraft';
import { useQueryParamValue } from '#src/app/shared/hooks/location';
import { useLoginState } from '#src/app/shared/hooks/session';
import { isLoaded, isLoading } from '#src/app/shared/utils/async';

import { claimSocialStarterPetitionAngles } from './api/claimSocialStarterPetitionAngles';
import { createPublishedPetition } from './api/createPublishedPetition';
import { usePrePublishTracking } from './tracking';
import { resolvePostPublishPath, resolvePublishSourceLocation } from './utils';

type LoginState = ReturnType<typeof useLoginState>;
type Draft = NonNullable<Exclude<ReturnType<typeof useStartAPetitionPageState>['data']['draft'], 'input' | 'metadata'>>;

type UsePrePublishReturn = ModelHookResult<
	{ isLoading: boolean; loginState: LoginState; error: Error | null },
	{ back: () => void; submit: () => void }
>;

export function usePrePublish(): UsePrePublishReturn {
	const { data, actions } = useStartAPetitionPageState();
	const { data: pageData } = useStartAPetitionPageData();

	const isKickstartExperimentEnabled = pageData.featureConfigs.petitionKickstartToggle;
	const isAmbassadorPostCreateFlowEnabled = pageData.featureConfigs.ambassadorPostCreateFlow;

	const utilityContext = useUtilityContext();
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const track = useTracking();
	const loginState = useLoginState();
	const { pathname, search } = useLocation();
	const sourceLocation = useQueryParamValue('source_location');

	const tracking = usePrePublishTracking();

	const {
		mutate: publishPetition,
		error: publishError,
		...mutationState
	} = useMutation({
		mutationFn: async (draft: Draft) => {
			// For social starter drafts, the intended staffing lives on the draft angle as
			// `metadata.source_location`; read it fresh (keyed by the draft's angleToken) and let it
			// override the lossy URL `source_location`. Non-SS drafts keep the URL value unchanged.
			const resolvedSourceLocation = await resolvePublishSourceLocation({
				angleToken: draft?.metadata?.angleToken,
				urlSourceLocation: sourceLocation,
				utilityContext,
			});
			const petition = await createPublishedPetition(
				{ petition: draft, isKickstartExperimentEnabled, sourceLocation: resolvedSourceLocation },
				utilityContext,
			);
			// Return the resolved source_location alongside the petition so tracking reports exactly what
			// was published (the metadata-derived code for SS drafts), not the possibly-lossy URL param.
			return { petition, resolvedSourceLocation };
		},
		onMutate: () => {
			void track('sap_step_submit', { step: 'prepublish', step_data: {}, step_data_raw: '{}' });
		},
		onSuccess: async ({ petition, resolvedSourceLocation }, draft: Draft) => {
			tracking.trackPrePublishActivatePetition({ petition, draft, sourceLocation: resolvedSourceLocation });
			tracking.trackConversion({ petition });

			const angleToken = draft?.metadata?.angleToken;
			if (angleToken) {
				// Explicit `=== false` (not `!videoConsent`): a missing/stale value (e.g. an old draft
				// started before this checkbox existed) must fail safe toward keeping source_url, never
				// toward clearing it.
				const clearSourceUrl = draft?.metadata?.videoConsent === false;
				// Awaited (not fire-and-forget): the claim also repoints the social starter's vanity
				// URL and staffs the petition server-side. If we navigated before this settled, the
				// starter onboarding page's isSocialStarter check could race a not-yet-staffed 404
				// and cache a false negative for 30 minutes, permanently hiding the gated share step.
				await claimSocialStarterPetitionAngles({
					token: angleToken,
					petitionId: petition.id,
					clearSourceUrl,
					utilityContext,
				});
			}

			deleteDraftAuthor({ storage: localStorage, queryClient });

			navigate(resolvePostPublishPath({ petition, resolvedSourceLocation, isAmbassadorPostCreateFlowEnabled }));
		},
		onError: (error) => {
			const err = error instanceof Error ? error : new Error('SAP: Failed to save petition');
			void track('activate_petition_error', { context: 'prepublish', error_type: err.message });
		},
	});

	const back = useCallback(() => {
		void track('sap_step_back', { step: 'prepublish' });

		actions.send({ type: 'back' });
	}, [actions, track]);

	const submit = useCallback(() => {
		if (loginState === 'AUTHENTICATED' && data.draft) {
			publishPetition(data.draft);
		} else {
			navigate(`/login_or_join?redirectTo=${encodeURIComponent(`${pathname}${search}`)}&user_flow=sap`);
		}
	}, [data.draft, publishPetition, navigate, loginState, pathname, search]);

	return {
		data: {
			isLoading: isLoading(mutationState) || isLoaded(mutationState),
			loginState,
			error: publishError,
		},
		actions: {
			back,
			submit,
		},
	};
}
